import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  ResponseDto,
  MembershipTierDto,
  UpdateMembershipTierDto,
} from "@mimber/schema"
import { membershipTiersKeys } from "../membership-tiers.utils"

type UpdateMembershipTierParams = {
  id: string
  data: UpdateMembershipTierDto
}

/**
 * Organization-specific hook for updating a membership tier
 * Can only be used in mimber-organization app
 */
export const useUpdateMembershipTier = (
  options?: UseMutationOptions<
    ResponseDto<MembershipTierDto>,
    Error,
    UpdateMembershipTierParams,
    unknown
  >
): UseMutationResult<
  ResponseDto<MembershipTierDto>,
  Error,
  UpdateMembershipTierParams
> => {
  guardAppUsage("mimber-organization", "useUpdateMembershipTier")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({
      id,
      data,
    }: UpdateMembershipTierParams): Promise<ResponseDto<MembershipTierDto>> => {
      const response = await server.patch<ResponseDto<MembershipTierDto>>(
        `membership-tiers/${id}`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate membership tiers queries to refetch the list and specific item
      queryClient.invalidateQueries({
        queryKey: membershipTiersKeys.findAll(),
      })
      queryClient.invalidateQueries({
        queryKey: membershipTiersKeys.findOne(variables.id),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
